/**
 * Katalog nástroje Asym requesty.
 *
 * Čistý modul bez Reactu — jde testovat samostatně a stránka z něj jen čte. Podmíněnost
 * polí je **deklarativní** (`showWhen`), ne rozeseta v runneru; vzor je `apiTools/catalog.ts`.
 * Runner tím zůstane generický a přidat pole znamená přidat řádek, ne větev v komponentě.
 *
 * Kanonickým zdrojem pravdy pro operace je serverový enum `AsymRequestOperation`; tenhle
 * seznam drží jen pořadí, popisky a to, které pole se má zobrazit.
 */

/** Skupina, do které operace v UI patří. */
export type AsymGroup = "token" | "tx" | "dr" | "auth";

export interface AsymOperationSpec {
  /** Hodnota posílaná na server (musí sedět s `AsymRequestOperation.getId()`). */
  id: string;
  /** i18n klíč popisku. */
  labelKey: string;
  group: AsymGroup;
  /** Operace vyžaduje `operationId` z předchozího kroku. */
  operationIdRequired?: boolean;
}

/** Pořadí je kurátorované — nejpoužívanější operace první. */
export const ASYM_OPERATIONS: AsymOperationSpec[] = [
  { id: "getPendingTransactions", labelKey: "asymtools.op.getPendingTransactions", group: "tx" },
  { id: "getInstanceInfo", labelKey: "asymtools.op.getInstanceInfo", group: "token" },
  { id: "approveTransaction", labelKey: "asymtools.op.approveTransaction", group: "tx" },
  { id: "rejectTransaction", labelKey: "asymtools.op.rejectTransaction", group: "tx" },
  { id: "deactivate", labelKey: "asymtools.op.deactivate", group: "token" },
  { id: "createDeviceReplacement", labelKey: "asymtools.op.createDeviceReplacement", group: "dr" },
  {
    id: "processDeviceReplacement",
    labelKey: "asymtools.op.processDeviceReplacement",
    group: "dr",
    operationIdRequired: true,
  },
  { id: "login", labelKey: "asymtools.op.login", group: "auth" },
];

/** Pořadí skupin v rozbalovacím seznamu. */
export const ASYM_GROUP_ORDER: AsymGroup[] = ["tx", "token", "dr", "auth"];

/** i18n klíč nadpisu skupiny. */
export function asymGroupLabelKey(group: AsymGroup): string {
  return `asymtools.group.${group}`;
}

/** Vyhledání operace podle id; `undefined`, když ji tahle verze nezná. */
export function findAsymOperation(id: string): AsymOperationSpec | undefined {
  return ASYM_OPERATIONS.find((op) => op.id === id);
}

// ==================== Nástroje (taby) ====================

export type AsymFieldKind =
  | "tokenSelect"
  | "operationSelect"
  | "text"
  | "textarea"
  | "number"
  | "select"
  | "passwordRadio";

export interface AsymFieldSpec {
  name: string;
  /** i18n klíč popisku. */
  label: string;
  kind: AsymFieldKind;
  required?: boolean;
  /** i18n klíč nápovědy. */
  hint?: string;
  /** Pro `select`: pevné volby (hodnota + i18n klíč nebo přímý text). */
  options?: { value: string; label: string }[];
  /** Podmíněné zobrazení: jen když jiné pole má danou hodnotu. */
  showWhen?: { field: string; value: string };
  /**
   * Pole se zobrazí jen tehdy, když vybraná operace vyžaduje `operationId`. Nejde vyjádřit
   * přes `showWhen` na pevnou hodnotu, protože podmínkou je vlastnost operace, ne konkrétní id.
   */
  showWhenOperationIdRequired?: boolean;
  default?: string;
  /** Vykreslit hodnotou v neproporcionálním písmu (blob, podpis). */
  mono?: boolean;
}

/** Identifikátor nástroje = tab. */
export type AsymToolId = "request" | "approve" | "signature" | "decrypt";

export interface AsymToolSpec {
  id: AsymToolId;
  /** i18n klíč popisku tabu. */
  label: string;
  /** i18n klíč vysvětlení, k čemu nástroj je. */
  hint: string;
  /** Cesta zákaznického API — vypisuje se, aby integrace viděla, co si má zavolat sama. */
  /**
   * Cesta ukázaná uživateli — **zákaznická**, protože obrazovka má učit, co má volat
   * integrace. Samotné volání jde přes obálku pod `/api/v1/test-tools/asym/**`, která
   * uvnitř provolá právě tenhle endpoint (viz `lib/asymTools.ts`).
   */
  endpoint: string;
  /** i18n klíč popisku spouštěcího tlačítka. */
  submit: string;
  fields: AsymFieldSpec[];
}

const TOKEN_FIELD: AsymFieldSpec = {
  name: "tokenId",
  label: "asymtools.token",
  kind: "tokenSelect",
  required: true,
};

const MESSAGE_FIELD: AsymFieldSpec = {
  name: "message",
  label: "asymtools.sign.message",
  kind: "textarea",
  required: true,
  hint: "asymtools.sign.messageHint",
  mono: true,
};

/**
 * Scoring a volba PINu jsou u obou podpisových nástrojů shodné — je to jeden kontrakt,
 * ne dva, takže i v UI je to jedna definice.
 */
const SIGNING_FIELDS: AsymFieldSpec[] = [
  MESSAGE_FIELD,
  { name: "scoringFlags", label: "asymtools.sign.scoringFlags", kind: "number", hint: "asymtools.sign.scoringFlagsHint" },
  {
    name: "recognitionType",
    label: "asymtools.sign.recognitionType",
    kind: "select",
    hint: "asymtools.sign.recognitionTypeHint",
    options: [
      { value: "", label: "asymtools.sign.recognitionType.unset" },
      { value: "0", label: "asymtools.sign.recognitionType.password" },
      { value: "1", label: "asymtools.sign.recognitionType.biometrics" },
    ],
  },
  { name: "passwordType", label: "asymtools.sign.password", kind: "passwordRadio", default: "valid" },
  {
    name: "userPassword",
    label: "asymtools.sign.userPassword",
    kind: "text",
    hint: "asymtools.sign.userPasswordHint",
    // Server override při záměrně chybném heslu ignoruje — pole, které nic nedělá,
    // se nemá nabízet, jinak si volající myslí, že podepsal svým heslem.
    showWhen: { field: "passwordType", value: "valid" },
  },
];

/** Pořadí tabů je kurátorované: nejdřív to, čím se začíná. */
export const ASYM_TOOLS: AsymToolSpec[] = [
  {
    id: "request",
    label: "asymtools.tool.request",
    hint: "asymtools.tool.request.hint",
    endpoint: "POST /api/v1/asym/request",
    submit: "asymtools.build",
    fields: [
      TOKEN_FIELD,
      { name: "operation", label: "asymtools.operation", kind: "operationSelect", required: true, default: "getPendingTransactions" },
      {
        name: "operationId",
        label: "asymtools.operationId",
        kind: "text",
        required: true,
        hint: "asymtools.operationIdHint",
        showWhenOperationIdRequired: true,
      },
    ],
  },
  {
    id: "approve",
    label: "asymtools.tool.approve",
    hint: "asymtools.tool.approve.hint",
    endpoint: "POST /api/v1/asym/approve-request",
    submit: "asymtools.tool.approve.submit",
    fields: [TOKEN_FIELD, ...SIGNING_FIELDS],
  },
  {
    id: "signature",
    label: "asymtools.tool.signature",
    hint: "asymtools.tool.signature.hint",
    endpoint: "POST /api/v1/asym/signature",
    submit: "asymtools.sign.submit",
    fields: [TOKEN_FIELD, ...SIGNING_FIELDS],
  },
  {
    id: "decrypt",
    label: "asymtools.tool.decrypt",
    hint: "asymtools.tool.decrypt.hint",
    endpoint: "POST /api/v1/asym/decrypt",
    submit: "asymtools.sign.decrypt",
    fields: [TOKEN_FIELD, MESSAGE_FIELD],
  },
];

export function findAsymTool(id: AsymToolId): AsymToolSpec {
  const tool = ASYM_TOOLS.find((t) => t.id === id);
  if (!tool) throw new Error(`Unknown asym tool: ${id}`);
  return tool;
}

/** Hodnoty formuláře — vše jako text, převod na typ dělá `buildAsymBody`. */
export type AsymValues = Record<string, string>;

/** Výchozí hodnoty nástroje; pole bez `default` začíná prázdné. */
export function defaultValues(tool: AsymToolSpec): AsymValues {
  const values: AsymValues = {};
  for (const field of tool.fields) {
    values[field.name] = field.default ?? "";
  }
  return values;
}

/**
 * Pole, která se mají zobrazit. Vyhodnocuje se nad aktuálními hodnotami, takže
 * `showWhen` i podmínka na `operationId` platí za běhu.
 */
export function visibleFields(tool: AsymToolSpec, values: AsymValues): AsymFieldSpec[] {
  return tool.fields.filter((field) => {
    if (field.showWhen && values[field.showWhen.field] !== field.showWhen.value) {
      return false;
    }
    if (field.showWhenOperationIdRequired) {
      return findAsymOperation(values.operation ?? "")?.operationIdRequired === true;
    }
    return true;
  });
}

/** `true`, když jsou vyplněná všechna povinná viditelná pole. */
export function isComplete(tool: AsymToolSpec, values: AsymValues): boolean {
  return visibleFields(tool, values)
    .filter((f) => f.required)
    .every((f) => (values[f.name] ?? "").trim().length > 0);
}

/**
 * Sestaví tělo požadavku.
 *
 * Posílá se jen to, co má význam: neviditelné pole (např. `operationId` u operace, která ho
 * nezná, nebo `userPassword` u záměrně chybného hesla) do těla nepatří. Server by ho sice
 * ignoroval, ale v komunikačním logu by to mátlo — a u `userPassword` by to navíc vypadalo,
 * že se jím podepisovalo.
 */
export function buildAsymBody(tool: AsymToolSpec, values: AsymValues): Record<string, unknown> {
  const visible = new Set(visibleFields(tool, values).map((f) => f.name));
  const body: Record<string, unknown> = {};

  const text = (name: string): string | undefined => {
    if (!visible.has(name)) return undefined;
    const value = (values[name] ?? "").trim();
    return value.length > 0 ? value : undefined;
  };

  body.tokenId = text("tokenId") ?? "";

  if (tool.id === "request") {
    body.operation = text("operation") ?? "";
    const operationId = text("operationId");
    if (operationId) body.operationId = operationId;
    return body;
  }

  body.message = text("message") ?? "";

  if (tool.id === "decrypt") {
    return body;
  }

  const scoringFlags = text("scoringFlags");
  if (scoringFlags !== undefined && Number.isFinite(Number(scoringFlags))) {
    body.scoringFlags = Number(scoringFlags);
  }
  const recognitionType = text("recognitionType");
  if (recognitionType !== undefined) {
    body.recognitionType = Number(recognitionType);
  }
  // Volba hesla je serverová — posílá se přepínač, nikdy heslo uložené u tokenu.
  body.useValidPassword = values.passwordType !== "invalid";
  const userPassword = text("userPassword");
  if (userPassword) body.userPassword = userPassword;

  return body;
}
